import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDeferredValue, useId, useRef, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import { StateView } from '@/card/record'
import { ApiError } from '@/lib/api'
import {
  AdminPage,
  AdminState,
  EditLayer,
  Field,
  LayerButtons,
  Notice,
  RowMenu,
  SearchBox,
  SkeletonTable,
  isForbidden,
  useAdminEditing,
} from './AdminShell'
import type { NumberSequence, ResetPeriod } from './api'
import { fetchSequences, previewSequence, updateSequence } from './api'

/**
 * Нумерация документов (тикет #97, макет AdmNumbering.dc.html).
 *
 * Маску проверяют глазами, поэтому в панели правки рядом с ней всегда пример следующего
 * номера — по новой маске, ещё до сохранения. Правила, по которым маска отвергается (нет
 * счётчика, номера повторятся после сброса), живут на сервере: здесь их нет намеренно,
 * иначе две копии правил разошлись бы при первой правке. Отказ показывается словами
 * сервера прямо под маской.
 *
 * Счётчик только показывается: понижать его API не умеет вовсе — это единственный способ
 * выдать уже выданные номера, — а повышения в API нет. Типы документов фиксированы,
 * поэтому кнопки «Добавить» на экране нет.
 */

const SECTIONS: Record<string, string[]> = {
  sales: ['quote', 'deal', 'sales_order', 'contract', 'support_ticket'],
  warehouse: ['goods_receipt', 'shipment', 'pick_task', 'stocktake', 'stock_transfer', 'stock_write_off',
    'goods_return', 'packing_list', 'revaluation', 'purchase_order'],
  finance: ['invoice', 'act', 'upd', 'torg12', 'invoice_factura', 'custody_bill'],
  hr: ['hr_order', 'hr_certificate'],
}
const TOKENS = ['{YYYY}', '{YY}', '{MM}', '{NNNNNN}', '-', '/']
const PERIODS: ResetPeriod[] = ['yearly', 'monthly', 'never']

export function NumberingPage() {
  const { t, locale } = useApp()
  const editing = useAdminEditing()
  const sequences = useQuery({ queryKey: ['admin-sequences'], queryFn: ({ signal }) => fetchSequences(signal) })
  const [query, setQuery] = useState('')
  const [section, setSection] = useState('all')
  const [open, setOpen] = useState<NumberSequence | null>(null)

  const list = sequences.data ?? []
  const docName = (sequence: NumberSequence) => t(`admin.documentType.${sequence.documentType}`)
  const needle = query.trim().toLowerCase()
  const shown = list
    .filter((sequence) => section === 'all' || (SECTIONS[section] ?? []).includes(sequence.documentType))
    .filter((sequence) => !needle || [docName(sequence), sequence.mask, sequence.documentType]
      .some((value) => value.toLowerCase().includes(needle)))
  const forbidden = isForbidden(sequences.error)

  return (
    <AdminPage
      titleKey="nav.adminNumbering"
      count={sequences.data ? t('adm.numbering.count', { count: list.length }) : undefined}
      leadKey="adm.numbering.lead"
      readOnly={!editing.mayEdit}
      roKey="adm.numbering.ro"
      forbidden={forbidden}
    >
      {!forbidden && (
        <div className="toolbar">
          <SearchBox value={query} onChange={setQuery} label={t('adm.numbering.search')} />
          <div className="seg" role="group" aria-label={t('adm.numbering.section')}>
            {['all', 'sales', 'warehouse', 'finance', 'hr'].map((key) => (
              <button key={key} type="button" aria-pressed={section === key} onClick={() => setSection(key)}>
                {t(`adm.numbering.section.${key}`)}
              </button>
            ))}
          </div>
        </div>
      )}
      <AdminState
        isLoading={sequences.isLoading}
        error={sequences.error}
        onRetry={() => void sequences.refetch()}
        forbiddenTitleKey="adm.numbering.forbidden"
        errorTitleKey="adm.numbering.error"
        skeleton={<SkeletonTable columns={5} rows={11} />}
      >
        {list.length === 0 ? (
          // The seeds create every sequence: an empty list means a migration did not run, and
          // documents would fail to get a number at all.
          <StateView boxed kind="setup" title={t('adm.numbering.setup')} description={t('adm.numbering.setup.text')} />
        ) : shown.length === 0 ? (
          <StateView boxed kind="empty" title={t('adm.numbering.empty', { query })}
                     description={t('adm.numbering.empty.text')}
                     actions={<button type="button" className="btn btn-secondary btn-sm" onClick={() => {
                       setQuery('')
                       setSection('all')
                     }}>{t('adm.resetFilters')}</button>} />
        ) : (
          <>
            <div className="tbl-wrap">
              <table className="tbl" aria-label={t('nav.adminNumbering')}>
                <thead>
                  <tr>
                    <th>{t('admin.numbering.documentType')}</th>
                    <th>{t('admin.numbering.mask')}</th>
                    <th>{t('adm.numbering.reset')}</th>
                    <th className="num">{t('adm.numbering.current')}</th>
                    <th>{t('admin.numbering.next')}</th>
                    {editing.mayEdit && <th className="adm-act"><span className="sr-only">{t('adm.actions')}</span></th>}
                  </tr>
                </thead>
                <tbody>
                  {shown.map((sequence) => (
                    <tr key={sequence.id} className={open?.id === sequence.id ? 'is-selected' : undefined}>
                      <td>
                        <span className="row" style={{ gap: 8 }}>
                          <Icon name="file" className="muted" />
                          <span style={{ fontWeight: 500 }}>{docName(sequence)}</span>
                          {sequence.scopeKey && <span className="muted t-sm">/ {sequence.scopeKey}</span>}
                        </span>
                      </td>
                      <td><span className="mono">{sequence.mask}</span></td>
                      <td>
                        <span className={sequence.resetPeriod === 'never' ? 'muted' : undefined}>
                          {t(`admin.numbering.reset.${sequence.resetPeriod}`)}
                        </span>
                      </td>
                      <td className="num">{sequence.currentValue.toLocaleString(locale)}</td>
                      <td><span className="mono">{sequence.nextExample}</span></td>
                      {editing.mayEdit && (
                        <td className="adm-act">
                          <RowMenu label={t('adm.numbering.actions', { doc: docName(sequence) })} items={[
                            { id: 'edit', label: t('admin.edit'), icon: 'adm-edit', onSelect: () => setOpen(sequence) },
                          ]} />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="t-sm muted">{t('adm.numbering.tokens')}</p>
          </>
        )}
      </AdminState>
      {open && <SequenceSheet sequence={open} onClose={() => setOpen(null)} />}
    </AdminPage>
  )
}

function SequenceSheet({ sequence, onClose }: { sequence: NumberSequence; onClose: () => void }) {
  const { t, locale } = useApp()
  const queryClient = useQueryClient()
  const formId = useId()
  const maskRef = useRef<HTMLInputElement>(null)
  const [mask, setMask] = useState(sequence.mask)
  const [reset, setReset] = useState<ResetPeriod>(sequence.resetPeriod)
  const [notice, setNotice] = useState<string | null>(null)
  const label = `${sequence.documentType}${sequence.scopeKey ? ` / ${sequence.scopeKey}` : ''}`
  const dirty = mask !== sequence.mask || reset !== sequence.resetPeriod

  // The example is the server's: it renders the mask by the same template the generator
  // uses, and refuses it by the same rules the save would.
  const typed = useDeferredValue(`${mask}\u0000${reset}`)
  const preview = useQuery({
    queryKey: ['admin-sequence-preview', sequence.id, typed],
    enabled: dirty && mask.trim() !== '',
    retry: false,
    queryFn: async () => (await previewSequence(mask, reset, sequence.currentValue)) ?? null,
  })
  const previewError = preview.error ? (preview.error instanceof ApiError ? preview.error.message : t('app.error')) : null
  const next = dirty ? (preview.data ?? null) : sequence.nextExample

  const save = useMutation({
    mutationFn: () => updateSequence(sequence.id, mask, reset),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-sequences'] })
      onClose()
    },
    onError: (error) => setNotice(error instanceof ApiError ? error.message : t('app.error')),
  })

  const insert = (token: string) => {
    const input = maskRef.current
    const at = input?.selectionStart ?? mask.length
    const end = input?.selectionEnd ?? at
    setMask(mask.slice(0, at) + token + mask.slice(end))
  }

  return (
    <EditLayer
      kind="sheet"
      overline={t('nav.adminNumbering')}
      title={t('adm.numbering.sheetTitle', { doc: t(`admin.documentType.${sequence.documentType}`) })}
      onClose={onClose}
      hint={previewError ? t('adm.numbering.fixFirst') : undefined}
      footer={<LayerButtons onCancel={onClose} saveLabel={t('admin.save')} pending={save.isPending}
                            disabled={!dirty || Boolean(previewError)} form={formId} />}
    >
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('adm.numbering.form')} onSubmit={(event) => {
        event.preventDefault()
        setNotice(null)
        save.mutate()
      }}>
        <Notice text={notice} />
        <Field label={t('admin.numbering.mask')} htmlFor="mk" required error={previewError} help={t('adm.numbering.mask.help')}>
          <input id="mk" ref={maskRef} className={previewError ? 'input mono is-invalid' : 'input mono'} aria-invalid={Boolean(previewError)}
                 aria-label={`${t('admin.numbering.mask')} ${label}`} value={mask} onChange={(e) => setMask(e.target.value)} />
          <span className="row-wrap" style={{ gap: 6 }}>
            <span className="t-xs muted">{t('adm.numbering.insert')}</span>
            {TOKENS.map((token) => (
              <button key={token} type="button" className="mask-tok" onClick={() => insert(token)}>{token}</button>
            ))}
          </span>
        </Field>
        <Field label={t('adm.numbering.reset')} htmlFor="rs" help={t('adm.numbering.reset.help')}>
          <select id="rs" className="input" aria-label={`${t('admin.numbering.reset')} ${label}`} value={reset}
                  onChange={(e) => setReset(e.target.value as ResetPeriod)}>
            {PERIODS.map((period) => (
              <option key={period} value={period}>{t(`admin.numbering.reset.${period}`)}</option>
            ))}
          </select>
        </Field>
        <div className="mask-preview" aria-live="polite">
          <span className="t-sm muted">{t('admin.numbering.next')}</span>
          <span className="next">{next ?? '—'}</span>
          {dirty && !previewError && preview.data && (
            <span className="flag flag-success"><Icon name="check" />{t('adm.numbering.previewOk')}</span>
          )}
          {dirty && !preview.data && !previewError && <span className="t-sm muted">{t('admin.numbering.notSaved')}</span>}
        </div>
        <Field label={t('adm.numbering.current')} help={t('adm.numbering.current.help')}>
          <div className="input is-readonly" aria-readonly="true" style={{ maxWidth: 200, justifyContent: 'flex-end' }}>
            {sequence.currentValue.toLocaleString(locale)}
          </div>
        </Field>
        <div className="alert alert-neutral">
          <Icon name="adm-info" />
          <span>{t('adm.numbering.note')}</span>
        </div>
      </form>
    </EditLayer>
  )
}
